// ── Effort on one ticket, for managers (Tech Mode part 2, 2 Oct) ───────────────
//
// Inside Ticket Detail, under the line items. Answers "what did this ticket
// actually take?": time worked against labor billed, the hourly rate that works
// out to against the target, who put the time in, and everything that happened
// to it through Spinboard in order.
//
// laborBilled comes from the ticket detail already on screen (labor lines net
// of discounts, helpers/totals.js); time, people and events from
// /api/effort/ticket/:id. The target is the server's blend of the people who
// worked the ticket (helpers/laborRates.js).

import React, { useState, useEffect } from 'react';
import { API, authHeaders } from '../utils/helpers';
import { fmtDur, fmtMoney, fmtRate, fmtWhen, eventLabel, colors, SectionLabel, DISPLAY_FONT } from './effortUi';

const MIN_SECONDS = 15 * 60;   // same floor as the Today board's Over budget list
const ENDED = { pause: 'Timer paused', stop: 'Timer stopped', auto: 'Timer stopped automatically', switch: 'Timer paused (switched tickets)' };

export default function EffortPanel({ ticketId, laborBilled, T, isDark }) {
  const c = colors(isDark);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null); setError(''); setShowAll(false);
    fetch(`${API}/api/effort/ticket/${encodeURIComponent(ticketId)}`, { credentials: 'include', headers: authHeaders() })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Could not load the effort on this ticket.'); return d; })
      .then(d => { if (live) setData(d); })
      .catch(err => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [ticketId]);

  const box = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
  if (error) return <div style={box}><SectionLabel T={T}>Effort</SectionLabel><div style={{ fontSize: 14, color: '#e74c3c' }}>{error}</div></div>;
  if (!data) return <div style={box}><SectionLabel T={T}>Effort</SectionLabel><div style={{ fontSize: 14, color: T.textSub }}>Loading…</div></div>;

  const secs = data.totalSeconds;
  const billed = typeof laborBilled === 'number' ? laborBilled : null;
  const rate = billed !== null && secs >= MIN_SECONDS ? billed / (secs / 3600) : null;
  const below = rate !== null && data.targetRate && rate < data.targetRate;
  const maxTech = Math.max(1, ...data.techs.map(t => t.seconds));

  // One timeline: timer sessions and ticket events, oldest first.
  const items = [];
  for (const s of data.sessions) {
    if (s.endReason === 'manual') {
      items.push({ at: s.startedAt, who: s.name, text: `Time added by hand · ${fmtDur((new Date(s.endedAt) - new Date(s.startedAt)) / 1000)}` });
      continue;
    }
    items.push({ at: s.startedAt, who: s.name, text: s.endedAt ? 'Timer started' : 'Timer started · running now', live: !s.endedAt });
    if (s.endedAt) {
      items.push({ at: s.endedAt, who: s.name,
        text: `${ENDED[s.endReason] || 'Timer stopped'} · ${fmtDur((new Date(s.endedAt) - new Date(s.startedAt)) / 1000)}${s.edited ? ' · edited' : ''}`,
        warn: s.endReason === 'auto' });
    }
  }
  for (const e of data.events) {
    const note = e.kind === 'sendback' && e.detail && e.detail.note ? ` · "${e.detail.note}"` : '';
    items.push({ at: e.at, who: e.name || 'Someone', text: eventLabel(e) + note, bad: e.kind === 'sendback' });
  }
  items.sort((a, b) => new Date(a.at) - new Date(b.at));
  const LIMIT = 8;
  const shown = showAll ? items : items.slice(-LIMIT);

  const tile = (label, value, sub, color) => (
    <div style={{ minWidth: 0 }}>
      <SectionLabel T={T}>{label}</SectionLabel>
      <div style={{ fontFamily: DISPLAY_FONT, fontSize: 26, fontWeight: 700, marginTop: 4, color: color || T.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: c.sub }}>{sub}</div>}
    </div>
  );

  return (
    <div style={box} aria-label="Effort on this ticket">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Effort</div>
        <div style={{ fontSize: 12, color: c.sub }}>Managers only</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        {tile('Time worked', fmtDur(secs), `${data.sessions.length} session${data.sessions.length === 1 ? '' : 's'}`)}
        {tile('Labor billed', billed === null ? '—' : fmtMoney(billed), 'labor lines, after discounts')}
        {tile(data.targetRate ? `Effective rate · target ${fmtRate(data.targetRate)}` : 'Effective rate',
          rate === null ? '—' : fmtRate(rate),
          rate === null ? (secs < MIN_SECONDS ? 'under 15 minutes worked' : 'labor not available') : !data.targetRate ? 'no target set' : below ? 'below target' : 'at or above target',
          rate === null || !data.targetRate ? undefined : below ? c.bad : c.good)}
      </div>

      {data.techs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SectionLabel T={T}>Time by tech</SectionLabel>
          {data.techs.map(t => (
            <div key={t.userId} style={{ display: 'grid', gridTemplateColumns: '140px 1fr auto', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
              <span style={{ height: 10, borderRadius: 5, background: T.barTrack || c.chip, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${Math.max(2, (t.seconds / maxTech) * 100)}%`, background: t.running ? '#2ecc71' : '#2a7ab8', borderRadius: 5 }} />
              </span>
              <span style={{ fontSize: 13, color: c.sub, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {fmtDur(t.seconds)} · {t.sessions} session{t.sessions === 1 ? '' : 's'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <SectionLabel T={T}>Timeline</SectionLabel>
          {items.length > LIMIT && (
            <button type="button" onClick={() => setShowAll(v => !v)}
              style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#5a9fd4', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
              {showAll ? 'Show latest only' : `Show all ${items.length}`}
            </button>
          )}
        </div>
        {items.length === 0 ? <div style={{ fontSize: 14, color: c.sub }}>Nothing recorded on this ticket in Spinboard yet.</div>
          : shown.map((it, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 120px 1fr', gap: 10, fontSize: 13, alignItems: 'baseline' }}>
              <span style={{ color: c.sub, whiteSpace: 'nowrap' }}>{fmtWhen(it.at)}</span>
              <span style={{ color: T.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.who}</span>
              <span style={{ color: it.bad ? c.bad : it.warn ? c.warn : it.live ? c.good : T.text }}>{it.text}</span>
            </div>
          ))}
      </div>

      <div style={{ fontSize: 12, color: c.sub }}>Status changes made directly in Lightspeed don't appear here.</div>
    </div>
  );
}
